import { Modal } from "@/frontend/shared/ui/Modal";
import styles from "./ManageExpertPermission.module.scss";
import { Typography } from "@/frontend/shared/ui/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { TCompanyData } from "@/frontend/features/Company/model/types/types";
import { useOutsideClick } from "@/frontend/shared/lib/hooks/useOutsideClick";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { useDebounce } from "@/frontend/shared/lib/useDebounce/useDebounce";
import { useQuery } from "@tanstack/react-query";
import { CompaniesDropdown, TFormattedSearchedCompanies } from "../CompaniesDropdown/CompaniesDropdown";
import { SearchInput } from "@/frontend/shared/ui/SearchInput";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { TrashIcon } from "@/frontend/shared/assets/icons";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { TOrganizationUsers } from "../../model/types/types";
import { getSearchExpertCompanies } from "../../model/services/getSearchExpertCompanies";
import { patchExpertAccess } from "../../model/services/patchExpertAccess";
import { AxiosError } from "axios";
import { getCompaniesForExperts } from "../../model/services/getCompaniesForExperts";
import { Logo } from "@/frontend/shared/ui/Logo";

export function ManageExpertPermission({
  isOpen,
  onClose,
  competitorsData,
  expertData,
  triggerRefetch
}: {
  isOpen: boolean;
  onClose: () => void;
  competitorsData?: TFormattedSearchedCompanies[];
  expertData?: TOrganizationUsers | null;
  triggerRefetch?: () => void;
}) {
  const { data: existedCompanies } = useQuery({
    queryKey: ["existedExpertCompanies", expertData?.id],
    queryFn: async () => {
      if (!expertData?.id) return [];
      const res = await getCompaniesForExperts({ id: expertData.id });
      const formatted: TFormattedSearchedCompanies[] =
        res?.map((company) => ({
          id: company.id,
          name: company.name ?? company.companyData.name,
          headline: company.description ?? company.companyData.description,
          logoUrl: company.companyData.logoUrl
        })) || [];

      return formatted;
    },
    refetchOnWindowFocus: false,
    enabled: isOpen,
    throwOnError: (error) => {
      console.error(error);
      snackbarConstructor("Error getting companies", "error");

      reportErrorToSentry(error, "EditCompetitorsModal: /organizations/experts");
      return false;
    }
  });

  useEffect(() => {
    if (existedCompanies) {
      setCompetitors(existedCompanies);
    }
  }, [existedCompanies]);

  const [companiesInputValue, setCompaniesInputValue] = useState("");
  const [companyValue, setCompanyValue] = useState("");
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [competitors, setCompetitors] = useState<TFormattedSearchedCompanies[]>(existedCompanies || []);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const [searchPage, setSearchPage] = useState(1);
  const [searchedCompanies, setSearchedCompanies] = useState<TCompanyData[]>([]);

  const {
    data: searchedCompaniesData,
    isFetching: isFetchingSearchedCompanies,
    refetch: refetchSearchedCompanies
  } = useQuery({
    queryKey: ["searchedExpertCompanies", companyValue, searchPage],
    queryFn: async () => {
      const res = await getSearchExpertCompanies({
        name: companyValue,
        page: searchPage
      });
      setSearchedCompanies(res?.data || []);
      return res;
    },
    refetchOnWindowFocus: false,
    enabled: isOpen,
    throwOnError: (error) => {
      console.error(error);

      snackbarConstructor("Error searching companies", "error");
      setSearchedCompanies([]);
      reportErrorToSentry(error, "SETTINGS: /portfolio-companies/search?query=${name}&page=${page}");
      return true;
    }
  });

  const formattedSearchedCompanies = useMemo(() => {
    if (searchedCompanies) {
      return searchedCompanies
        ?.filter(
          (company: TCompanyData) => company.name && competitors && !competitors.some((c) => c.id === company.id)
        )
        ?.map((company: TCompanyData) => ({
          id: company.id,
          name: company.name ?? "",
          headline: company.headline ?? "",
          logoUrl: company.logoUrl ?? "",
          website: company.website ?? "",
          linkedInUrl: company.linkedInUrl ?? ""
        }));
    }

    return [];
  }, [searchedCompanies, competitors]);

  useEffect(() => {
    setSearchPage(1);
    setSearchedCompanies([]);
  }, [companyValue]);

  const debouncedSearch = useDebounce((value: string) => {
    setCompanyValue(value);
  }, 400);

  useOutsideClick(dropdownRef, () => {
    setDropdownOpen(false);
  });

  function handleInputChange(value: string) {
    setCompaniesInputValue(value);
    debouncedSearch(value);
  }

  function onKeyDownEnter(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      refetchSearchedCompanies();
      setDropdownOpen(true);
    }
  }

  function onConfirmDiscardChanges() {
    onClose();
    setCompetitors(competitorsData || []);
    setCompaniesInputValue("");
    setCompanyValue("");
    setIsOpenConfirmationModal(false);
  }

  function compareCompetitors(
    competitors: TFormattedSearchedCompanies[],
    competitorsData: TFormattedSearchedCompanies[]
  ): boolean {
    for (const company of competitorsData) {
      if (!company.id) {
        return false;
      }
    }

    if (competitors.length !== competitorsData.length) {
      return false;
    }

    for (let i = 0; i < competitors.length; i++) {
      const originalCompany = competitors[i];
      const comparedCompany = competitorsData[i];

      if (
        originalCompany.id !== comparedCompany.id ||
        originalCompany.name !== comparedCompany.name ||
        originalCompany.headline !== comparedCompany.headline
      ) {
        return false;
      }
    }

    return true;
  }

  function onCloseModal() {
    if (!compareCompetitors(competitors, competitorsData || [])) {
      setIsOpenConfirmationModal(true);
    } else {
      onClose();
    }
  }

  const { firstName, lastName, organizationId, id: expertId, createdAt, email } = expertData || {};

  async function onSave() {
    if (!expertId) {
      return;
    }

    if (!compareCompetitors(competitors, competitorsData || [])) {
      onClose();
    }

    setIsSavingChanges(true);
    try {
      await patchExpertAccess({
        expertId: expertId || 0,
        portfolioCompanyIds: competitors.map((c) => c.id)
      });
      triggerRefetch?.();
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<{ message: string }>;
      reportErrorToSentry(axiosError, "MANAGE EXPERT PERMISSION MODAL: /organizations/experts");
      const errorMessage = axiosError.response?.data?.message || "Unexpected error occurred";
      snackbarConstructor(errorMessage, "error");
    }
    setIsSavingChanges(false);
    onClose();
    setIsOpenConfirmationModal(false);
    setCompaniesInputValue("");
    setDropdownOpen(false);
    setCompanyValue("");
    setCompetitors([]);
  }

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} title="Manage Permission" className={styles.modal} width="560px">
      <div className={styles.container}>
        <div className={styles.permissionContainer}>
          <div className={styles.image}>
            <Typography variant="label-4" color="greyscale700">
              {firstName && lastName && firstName?.slice(0, 1).toUpperCase() + lastName?.slice(0, 1).toUpperCase()}
            </Typography>
          </div>
          <div className={styles.permissionInfoContainer}>
            <Typography variant="label-4" color="greyscale700">
              {firstName} {lastName}
            </Typography>
            <Typography variant="body-3" color="greyscale500">
              {email}
            </Typography>
          </div>
        </div>
        <div className={styles.divider} />
        <Typography variant="label-3" color="greyscale800">
          Companies Access
        </Typography>

        <div className={styles.searchContainer} ref={dropdownRef}>
          <SearchInput
            placeholder="Add companies"
            value={companiesInputValue}
            onChange={(v) => handleInputChange(v)}
            onClick={() => setDropdownOpen(true)}
            className={styles.searchInput}
            onKeyDown={onKeyDownEnter}
          />
          <CompaniesDropdown
            className={styles.companiesContainer}
            inputValue={companiesInputValue}
            options={formattedSearchedCompanies}
            isOpen={isDropdownOpen}
            setInputValue={setCompaniesInputValue}
            onClose={() => setDropdownOpen(false)}
            isCompaniesLoading={isFetchingSearchedCompanies}
            setCompetitorsData={setCompetitors}
            loadMore={() => {
              if (searchedCompaniesData?.meta?.hasNext) setSearchPage((prev) => prev + 1);
            }}
          />
        </div>
        {!!competitors && competitors.length > 0 && (
          <div className={styles.competitorsContainer}>
            {competitors.map((company) => (
              <div key={company.id} className={styles.competitor}>
                <div className={styles.leftsideContainer}>
                  {company.logoUrl ? (
                    <Logo logo={company.logoUrl} alt={company.name} />
                  ) : (
                    <div className={styles.companyImg} />
                  )}
                  <div className={styles.infoContainer}>
                    <Typography variant="label-4" color="greyscale800" className={styles.name}>
                      {company.name}
                    </Typography>
                    <Typography variant="body-3" color="greyscale600" className={styles.headline}>
                      {company.headline}
                    </Typography>
                  </div>
                </div>
                <TrashIcon
                  onClick={() => setCompetitors((prev) => prev.filter((c) => c.id !== company.id))}
                  className={styles.removeIcon}
                />
              </div>
            ))}
          </div>
        )}
        <div className={styles.bottomContainer}>
          <div className={styles.bottomDivider} />
          <NormalButton onClick={onSave} className={styles.btn} size="medium" isSubmitting={isSavingChanges}>
            Save
          </NormalButton>
        </div>
      </div>
      <ConfirmationModal
        isOpen={isOpenConfirmationModal}
        onClose={() => setIsOpenConfirmationModal(false)}
        onConfirm={onConfirmDiscardChanges}
        content={"Are you sure you want to discard the changes you made?"}
        title={"Discard Changes"}
        confirmButtonText={"Discard"}
        cancelButtonText={"No Thanks"}
        dangerConfirm
      />
    </Modal>
  );
}
